// Recording flow UI — #163 PR B.
//
// Lazy-loaded module that renders the pre-recording consent disclosure
// modal, drives the MediaRecorder lifecycle via screen-recording-client,
// and surfaces the live timer + Stop button while recording. Returns the
// final RecordingResult on success, or null on cancel / error.
//
// Bundle hygiene: this module is dynamic-imported from ui.ts only when
// the user clicks the Record button, so the consent-modal markup and the
// recording-overlay markup don't ship in the always-loaded widget bundle.
// Inline styles (no shared CSS) keep the module self-contained — Vite
// emits one chunk file under dist/chunks/ on first Record click.
//
// Compliance note (per ticket BLOCKER): the consent modal MUST appear and
// the user MUST accept it BEFORE getDisplayMedia is called. This function
// enforces that ordering — startRecording() only runs after the modal
// resolves with `true`.

// #482: reuse the widget's single Trusted Types policy bridge so the consent
// modal's innerHTML assignment doesn't throw under a host CSP that enforces
// `require-trusted-types-for 'script'`. Shared util (csp.ts) — no import cycle.
import { setTrustedHtml } from './csp.ts';
import { en } from './i18n/en.ts';
import type { SupportedLocale, WidgetMessages } from './i18n/index.ts';
import {
  startRecording as defaultStartRecording,
  type RecordingResult,
} from './screen-recording-client.ts';

export interface RecordingFlowOptions {
  /** ShadowRoot the widget renders into. Modal + overlay get appended here. */
  shadow: ShadowRoot;
  /** Workspace display name shown in the consent disclosure. */
  workspaceName: string;
  /**
   * Retention period in days, shown in the disclosure copy. Caller passes
   * the workspace's current `attachment_retention_days` so the modal text
   * matches reality (default 90 if not yet customized).
   */
  retentionDays: number;
  /** Pluggable for tests. Defaults to screen-recording-client.startRecording. */
  startRecording?: typeof defaultStartRecording;
  /**
   * #163 PR C — best-effort callback fired at the three lifecycle moments
   * (consent_accepted, started, stopped). Caller wires this to
   * api.submitWidgetEvent so the events land in auth_events. Optional —
   * when omitted, recording works but no audit row gets logged.
   */
  onAuditEvent?: (event: {
    type: 'recording_consent_accepted' | 'recording_started' | 'recording_stopped';
    metadata?: Record<string, unknown>;
  }) => void;
  /**
   * #56: i18n message dictionary. Falls back to English if absent so legacy
   * embeds (or older bootstrap paths) still produce coherent text. Caller
   * threads this from bootstrap → ui.ts → here.
   */
  messages?: WidgetMessages;
  /**
   * #56: BCP-47 locale for the modal markup (used as the `lang` attribute
   * on the dialog element so screen readers + browser TTS pronounce in the
   * right language). Defaults to 'en'.
   */
  locale?: SupportedLocale;
  /**
   * Set when the caller has ALREADY shown the consent modal and fired the
   * `recording_consent_accepted` audit event itself. Used by the single
   * "Record screen" entry point, which must read the modal's "Include camera"
   * choice before it can pick between this flow and the camera flow.
   *
   * Compliance ordering is unchanged: consent is still collected and recorded
   * before any getDisplayMedia / getUserMedia call, just one level up the
   * stack. Leave unset and the flow prompts for consent itself.
   */
  skipConsent?: boolean;
}

/** Outcome of the pre-recording consent step. */
export interface ConsentOutcome {
  /** False when the reporter cancelled, pressed Escape, or dismissed. */
  accepted: boolean;
  /**
   * Whether the reporter ticked "Include camera". Always false when the
   * modal was opened without `cameraOption`, so a caller that cannot support
   * camera capture can ignore this field entirely.
   */
  includeCamera: boolean;
}

/**
 * Run the full recording flow start-to-finish. Returns the final result
 * blob + metadata on a successful record-and-stop cycle, or null if the
 * user cancels at any step (consent decline, screen-pick dismiss, manual
 * cancel during recording).
 */
export async function runRecordingFlow(
  opts: RecordingFlowOptions,
): Promise<RecordingResult | null> {
  if (!opts.skipConsent) {
    const consent = await showConsentModal(opts);
    if (!consent.accepted) return null;
    // Compliance signal — fired BEFORE startRecording (= getDisplayMedia)
    // so we record consent even if the browser screen-pick is dismissed.
    opts.onAuditEvent?.({ type: 'recording_consent_accepted' });
  }

  const startFn = opts.startRecording ?? defaultStartRecording;
  let controller: Awaited<ReturnType<typeof defaultStartRecording>>;
  try {
    controller = await startFn({});
  } catch {
    // Browser screen-pick dismissed, support check failed mid-call, etc.
    return null;
  }
  opts.onAuditEvent?.({ type: 'recording_started' });

  const userStopped = await showRecordingOverlay(
    opts.shadow,
    controller,
    opts.messages ?? en,
    opts.locale,
  );
  if (!userStopped) {
    controller.cancel();
    opts.onAuditEvent?.({
      type: 'recording_stopped',
      metadata: { resolution: 'cancelled' },
    });
    return null;
  }
  const result = await controller.stop();
  opts.onAuditEvent?.({
    type: 'recording_stopped',
    metadata: {
      resolution: 'completed',
      duration_ms: result.durationMs,
      auto_stopped: result.autoStopped,
      audio_captured: result.audioCaptured,
    },
  });
  return result;
}

// ============================================================================
// Consent modal
// ============================================================================

/**
 * Shared consent step for both recording paths.
 *
 * `cameraOption: true` renders an "Include camera" checkbox in the modal and
 * lets the reporter upgrade a screen recording to screen + camera at the
 * moment they consent. The camera disclosure bullet appears and disappears
 * with the checkbox, so the disclosure always describes what is actually
 * about to be captured rather than listing a surface the reporter declined.
 *
 * `cameraDisclosure: true` pins the camera bullet on with no checkbox — for a
 * caller that has already committed to capturing the camera.
 */
export function showConsentModal(
  opts: RecordingFlowOptions & {
    cameraDisclosure?: boolean;
    cameraOption?: boolean;
    /** Audio-only uses the same privacy disclosure without screen-share copy. */
    captureMode?: 'audio';
  },
): Promise<ConsentOutcome> {
  const m = opts.messages ?? en;
  const wsName = opts.workspaceName.length > 0 ? opts.workspaceName : 'this site';
  const audioOnly = opts.captureMode === 'audio';
  return new Promise<ConsentOutcome>((resolve) => {
    const overlay = document.createElement('div');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'oc-rec-consent-title');
    if (opts.locale) overlay.setAttribute('lang', opts.locale);
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      background: 'rgba(0,0,0,0.55)',
      zIndex: '2147483646',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
    });

    const dialog = document.createElement('div');
    Object.assign(dialog.style, {
      background: '#ffffff',
      color: '#111827',
      borderRadius: '8px',
      maxWidth: '440px',
      width: '100%',
      padding: '24px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
      fontFamily: 'system-ui, sans-serif',
    });

    const title = document.createElement('h2');
    title.id = 'oc-rec-consent-title';
    title.textContent = audioOnly ? m.recordingAudioConsentTitle : m.recordingConsentTitle;
    Object.assign(title.style, {
      margin: '0 0 12px',
      fontSize: '18px',
      fontWeight: '600',
    });

    const body = document.createElement('div');
    body.style.fontSize = '14px';
    body.style.lineHeight = '1.5';
    body.style.color = '#4b5563';
    // Lead paragraph: workspace name is interpolated by the locale function
    // and re-html-escaped inline so a malicious workspace.name can't inject.
    const leadHtml = escapeHtml(
      audioOnly ? m.recordingAudioConsentLead(wsName) : m.recordingConsentLead(wsName),
    );
    // #297 — camera bullet sits ahead of the mic bullet. Keeping the text in
    // i18n means localized rendering "just works" like the other bullets.
    //
    // The element is always emitted and toggled via `hidden` rather than being
    // conditionally interpolated, because with `cameraOption` the reporter can
    // flip the camera decision while the modal is open and the disclosure has
    // to track it. Pinned visible for `cameraDisclosure`, hidden by default for
    // `cameraOption` (the checkbox starts unticked), absent otherwise.
    const cameraBullet = `  <li data-oc-camera-bullet${opts.cameraDisclosure ? '' : ' hidden'}>${escapeHtml(m.recordingConsentBulletCamera)}</li>\n`;
    // #482: TT-safe assignment. leadHtml + every bullet is escaped above.
    setTrustedHtml(
      body,
      [
        `<p style="margin:0 0 12px;">${leadHtml}</p>`,
        `<ul style="margin:0 0 16px;padding-left:20px;">`,
        `  <li>${escapeHtml(m.recordingConsentBulletAdminsOnly)}</li>`,
        `${opts.cameraDisclosure || opts.cameraOption ? cameraBullet : ''}  <li>${escapeHtml(m.recordingConsentBulletMicAudio)}</li>`,
        `  <li>${escapeHtml(m.recordingConsentBulletMaxLength)}</li>`,
        `  <li>${escapeHtml(m.recordingConsentBulletRetention(opts.retentionDays))}</li>`,
        `  <li>${escapeHtml(m.recordingConsentBulletDeletion)}</li>`,
        `</ul>`,
        // Concrete tip for Chrome's "Choose what to share" picker. The dialog
        // can't be skipped (browser security), but reporters often don't know
        // that "Chrome Tab" is the right choice. We pre-bias the picker via
        // displaySurface + preferCurrentTab in the recording client; this copy
        // tells them what to click when it appears.
        audioOnly
          ? `<p style="margin:0;font-size:13px;color:#6b7280;">${escapeHtml(m.recordingAudioConsentTip)}</p>`
          : `<p style="margin:0;font-size:13px;color:#6b7280;">Tip: when the browser asks what to share, choose <strong>Chrome Tab</strong> → <strong>this page</strong>, then click <strong>Share</strong>.</p>`,
      ].join(''),
    );

    // "Include camera" opt-in. Only built when the caller offered it, which
    // in turn only happens when checkCameraRecordingSupport() said yes — so a
    // browser or iframe that cannot reach the camera never shows a control
    // that would fail on click. Label wraps the input (same a11y pattern the
    // form uses) so no id/for pairing is needed inside the shadow root.
    let cameraCheckbox: HTMLInputElement | null = null;
    let cameraRow: HTMLLabelElement | null = null;
    if (opts.cameraOption) {
      cameraRow = document.createElement('label');
      Object.assign(cameraRow.style, {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        margin: '0 0 4px',
        fontSize: '14px',
        color: '#374151',
        cursor: 'pointer',
      });
      cameraCheckbox = document.createElement('input');
      cameraCheckbox.type = 'checkbox';
      cameraCheckbox.setAttribute('data-oc-include-camera', '');
      cameraCheckbox.style.cursor = 'pointer';
      const cameraText = document.createElement('span');
      // textContent, not innerHTML — locale strings are developer-authored but
      // this keeps the modal free of any HTML-injection surface.
      cameraText.textContent = m.recordingConsentIncludeCameraLabel;
      cameraRow.appendChild(cameraCheckbox);
      cameraRow.appendChild(cameraText);

      // Keep the disclosure honest as the reporter toggles the box.
      const bullet = body.querySelector<HTMLLIElement>('[data-oc-camera-bullet]');
      cameraCheckbox.addEventListener('change', () => {
        if (bullet) bullet.hidden = !cameraCheckbox?.checked;
      });
    }

    const buttonRow = document.createElement('div');
    Object.assign(buttonRow.style, {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '8px',
      marginTop: '16px',
    });

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.textContent = m.recordingConsentCancelBtn;
    Object.assign(cancelBtn.style, {
      padding: '8px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      background: '#ffffff',
      color: '#374151',
      fontSize: '14px',
      cursor: 'pointer',
    });

    const acceptBtn = document.createElement('button');
    acceptBtn.type = 'button';
    acceptBtn.textContent = m.recordingConsentAcceptBtn;
    Object.assign(acceptBtn.style, {
      padding: '8px 16px',
      border: 'none',
      borderRadius: '4px',
      background: '#2f2e40',
      color: '#ffffff',
      fontSize: '14px',
      cursor: 'pointer',
    });

    // #163 PR D — focus trap. Cache the element that was focused before the
    // modal opened so we can return focus to it on close. WCAG 2.1 dialog
    // pattern: focus must NOT escape the modal while it's open, and MUST
    // return to the trigger when the modal closes.
    const previouslyFocused =
      typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    function close(accepted: boolean): void {
      // Cast: ShadowRoot.addEventListener / removeEventListener inherit
      // EventTarget's generic signature (`(evt: Event) => void`) and lack
      // the typed-event overloads HTMLElement/Document expose for 'keydown'.
      // onKeyDown is strongly typed internally; we cast only at the
      // registration boundary.
      opts.shadow.removeEventListener('keydown', onKeyDown as EventListener);
      overlay.remove();
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        try {
          previouslyFocused.focus();
        } catch {
          // Trigger element was removed from the DOM during the modal's
          // lifetime — nothing to focus back to. Acceptable degraded path.
        }
      }
      // Read the checkbox before the overlay is garbage — and only report a
      // camera opt-in on an ACCEPTED consent, so a reporter who ticks the box
      // and then cancels never has that read as permission.
      resolve({
        accepted,
        includeCamera: accepted && Boolean(cameraCheckbox?.checked),
      });
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        close(false);
        return;
      }
      // Focus trap: Tab + Shift+Tab cycle within the modal's controls only.
      // Without the trap, Tab from the last control would escape into the
      // page behind the modal — a WCAG 2.1 dialog violation. The camera
      // checkbox joins the cycle first when present, matching its DOM order.
      //
      // Shadow DOM gotcha: `document.activeElement` returns the shadow HOST,
      // not the focused element inside the shadow root. Use
      // `shadow.activeElement` to peer into the shadow.
      if (event.key === 'Tab') {
        const focusables: HTMLElement[] = cameraCheckbox
          ? [cameraCheckbox, cancelBtn, acceptBtn]
          : [cancelBtn, acceptBtn];
        const focused = opts.shadow.activeElement as HTMLElement | null;
        const idx = focused ? focusables.indexOf(focused) : -1;
        if (event.shiftKey) {
          // Shift+Tab from first focusable wraps to last.
          if (idx <= 0) {
            event.preventDefault();
            focusables[focusables.length - 1]?.focus();
          }
        } else {
          // Tab from last focusable wraps to first.
          if (idx === focusables.length - 1) {
            event.preventDefault();
            focusables[0]?.focus();
          }
        }
      }
    }

    cancelBtn.addEventListener('click', () => close(false));
    acceptBtn.addEventListener('click', () => close(true));
    // #326: listener attached to the shadow root, not `document`. The modal
    // overlay is appended to opts.shadow below, so Escape/Tab events from the
    // focused buttons bubble through the shadow tree and fire here before
    // crossing the shadow boundary. Required for compatibility with mountUi's
    // shadow-root keystroke isolation, which calls stopPropagation on every
    // key event leaving the shadow — a document-level listener would never
    // see them. See close() above for the cast rationale.
    opts.shadow.addEventListener('keydown', onKeyDown as EventListener);

    buttonRow.appendChild(cancelBtn);
    buttonRow.appendChild(acceptBtn);
    dialog.appendChild(title);
    dialog.appendChild(body);
    if (cameraRow) dialog.appendChild(cameraRow);
    dialog.appendChild(buttonRow);
    overlay.appendChild(dialog);
    opts.shadow.appendChild(overlay);

    // Focus the accept button so keyboard users land on the action they're
    // here to take. Screen readers announce the dialog title via
    // aria-labelledby.
    acceptBtn.focus();
  });
}

// ============================================================================
// Recording overlay (timer + stop button)
// ============================================================================

export interface OverlayController {
  state: 'recording' | 'stopped' | 'error';
  stopped: boolean;
  cancel(): void;
  stop(): Promise<unknown>;
  onStateChange(cb: (state: string) => () => void): () => void;
}

/**
 * Resolves to true when the user clicked Stop (so caller should call
 * controller.stop() and accept the result), or false when the recording
 * ended without user interaction (auto-stop, browser stop-share, error).
 *
 * Either way, the overlay DOM is torn down before resolution so the caller
 * can render its next UI without a stale modal lingering.
 *
 * Exported so the camera flow (#297 PR C) reuses the same overlay UI
 * verbatim — same timer, same stop button, same a11y posture.
 */
export function showRecordingOverlay(
  shadow: ShadowRoot,
  controller: OverlayController,
  m: WidgetMessages,
  locale: SupportedLocale | undefined,
): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const overlay = document.createElement('div');
    // #163 PR D: role="status" without aria-live on the overlay — the visible
    // timer ticks every second, which would flood screen readers if announced.
    // The dedicated `announcer` span below carries the polite live region with
    // a 10-second cadence per ticket AC.
    overlay.setAttribute('role', 'status');
    Object.assign(overlay.style, {
      position: 'fixed',
      bottom: '24px',
      right: '24px',
      background: '#111827',
      color: '#ffffff',
      borderRadius: '8px',
      padding: '12px 16px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
      fontFamily: 'system-ui, sans-serif',
      zIndex: '2147483646',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    });

    const dot = document.createElement('span');
    Object.assign(dot.style, {
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      background: '#ef4444',
      boxShadow: '0 0 0 2px rgba(239,68,68,0.35)',
      flexShrink: '0',
    });

    const timer = document.createElement('span');
    // #163 PR D: visible timer is sighted-user UI only. Hide from AT so the
    // 1-second ticks don't drown out everything else; the announcer below
    // handles the AT-facing 10-second cadence.
    timer.setAttribute('aria-hidden', 'true');
    if (locale) timer.setAttribute('lang', locale);
    timer.textContent = m.recordingTimerIdle;
    Object.assign(timer.style, {
      fontSize: '14px',
      fontWeight: '500',
      minWidth: '120px',
    });

    // #163 PR D: visually-hidden ARIA live region with a 10-second update
    // cadence. Per ticket AC: "Recording timer announces every 10s via
    // VoiceOver." Visible timer keeps 1s ticks for sighted users.
    const announcer = document.createElement('span');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.setAttribute('role', 'timer');
    Object.assign(announcer.style, {
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: '0',
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0 0 0 0)',
      clipPath: 'inset(50%)',
      whiteSpace: 'nowrap',
      border: '0',
    });
    if (locale) announcer.setAttribute('lang', locale);
    announcer.textContent = m.recordingStartedAnnouncement;

    const stopBtn = document.createElement('button');
    stopBtn.type = 'button';
    stopBtn.textContent = m.recordingStopBtn;
    Object.assign(stopBtn.style, {
      padding: '6px 12px',
      border: 'none',
      borderRadius: '4px',
      background: '#ef4444',
      color: '#ffffff',
      fontSize: '13px',
      fontWeight: '500',
      cursor: 'pointer',
    });

    const startMs = Date.now();
    const tickHandle = setInterval(() => {
      const elapsedSec = Math.floor((Date.now() - startMs) / 1000);
      const mm = Math.floor(elapsedSec / 60);
      const ss = elapsedSec % 60;
      timer.textContent = m.recordingTimer(mm, ss);
    }, 1000);

    // 10-second cadence for the AT announcer. First update fires at the
    // 10-second mark; the initial start announcement is the launch signal.
    const announceHandle = setInterval(() => {
      const elapsedSec = Math.floor((Date.now() - startMs) / 1000);
      announcer.textContent = m.recordingProgressAnnouncement(elapsedSec);
    }, 10000);

    function teardown(): void {
      clearInterval(tickHandle);
      clearInterval(announceHandle);
      overlay.remove();
    }

    stopBtn.addEventListener('click', () => {
      teardown();
      resolve(true);
    });

    // External stop (auto-stop at 5:00, browser "Stop sharing", error) —
    // resolve false so caller doesn't double-stop the controller.
    const unsubscribe = controller.onStateChange((state) => {
      if (state === 'stopped' || state === 'error') {
        teardown();
        unsubscribe();
        resolve(false);
      }
      return unsubscribe;
    });

    overlay.appendChild(dot);
    overlay.appendChild(timer);
    overlay.appendChild(announcer);
    overlay.appendChild(stopBtn);
    shadow.appendChild(overlay);

    stopBtn.focus();
  });
}

// ============================================================================
// Helpers
// ============================================================================

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
